import React, { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { motion } from 'framer-motion'
import { useGetAgencyInvoiceHistory } from '../../../api/agencies/useGetAgencyInvoiceHistory'
import { formatCurrency } from '../../../lib/utils/formatNumber'
import { Pill, Table, TextInput } from '@demandlocal/components'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import FiltersContext from '../../../components/Filters/FiltersContext'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { ReactComponent as ActionDownload } from '../../../images/icons/action_download.svg'
import { useGetAgencyInvoiceList } from '../../../api/agencies/useGetAgencyInvoiceList'
import StackedBarChartV2 from '../../../components/Charts/StackedBarChartV2'
import { useDownloadInvoice } from '../../../api/agencies/useDownloadInvoice'
import toast from 'react-hot-toast'

type InvoiceDetail = {
  file_name: string
  file_path: string
  open_amount: number
  month: string
}

const columnHelper = createColumnHelper<InvoiceDetail>()

export const Invoices = (): JSX.Element => {
  const { agency_id } = useParams()
  const { data: { data } = {}, isLoading: isLoadingInvoicesHistory } =
    useGetAgencyInvoiceHistory(Number(agency_id))
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [filters, setFilters] = useState<Partial<InvoiceDetail>>({
    month: dayjs().subtract(1, 'months').format('YYYY-MM'),
  })

  const { mutateAsync: downloadInvoice } = useDownloadInvoice()

  const isOverdueMonth = useCallback(
    (month: string) => {
      return data
        ?.filter((entry) => entry.open_amount > 0)
        .map((entry) => entry.month)
        .filter((month) => dayjs(month).isBefore(dayjs().startOf('month')))
        ?.includes(dayjs(month, 'YYYY-MM').format('YYYY-MM-DD'))
    },
    [data],
  )

  const isUpcomingAndNotPaid = useCallback(
    (month: string) => {
      return data
        ?.filter((entry) => entry.open_amount > 0)
        .map((entry) => entry.month)
        .filter((month) =>
          dayjs(month).isAfter(dayjs().startOf('month').subtract(1, 'month')),
        )
        ?.includes(dayjs(month, 'YYYY-MM').format('YYYY-MM-DD'))
    },
    [data],
  )

  const downloadPDF = useCallback(
    async (file_name: string, agency_id: string, path: string) => {
      const toastId = toast.loading('Downloading Invoice')
      try {
        await downloadInvoice(
          {
            data: {
              agency_id: agency_id,
              path: path,
            },
          },
          {
            onSuccess: async (data) => {
              const blob = await data.blob()
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `${file_name}`
              document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
              a.click()
              a.remove()
              toast.error('Successfully Downloaded Invoice', {
                id: toastId,
                duration: 5000,
              })
            },
            onError: () => {
              toast.error('Error Downloading Invoice', { id: toastId, duration: 5000 })
            },
          },
        )
      } catch (e) {
        toast.error('Error Downloading Invoice', { id: toastId, duration: 5000 })
      }
    },
    [downloadInvoice],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('file_name', {
        header: 'Name',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          if (isOverdueMonth(row.original.month)) return <Pill type="error">OVERDUE</Pill>
          else if (isUpcomingAndNotPaid(row.original.month))
            return <Pill type="blue">UPCOMING</Pill>
          else return <Pill type="gray">PAID</Pill>
        },
      }),
      columnHelper.accessor('month', {
        header: 'Date',
        cell: ({ row }) =>
          dayjs(row.original?.month, 'YYYY-MM').format('MMMM YYYY') ?? 'N/A',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Download',
        cell: ({ row }) => (
          <div className="flex flex-row justify-center max-w-[70px]">
            <ActionDownload
              className="fill-primary-persian-blue cursor-pointer"
              onClick={() =>
                downloadPDF(
                  row.original.file_name,
                  agency_id ?? '',
                  row.original.file_path,
                )
              }
            />
          </div>
        ),
      }),
    ],
    [agency_id, downloadPDF, isOverdueMonth, isUpcomingAndNotPaid],
  )

  const remappedDataForBarChart = useMemo(() => {
    const invoice_total_data = (data ?? []).map((d) => ({
      month: d.month,
      amount: d.invoice_total,
      amountType: 'Invoice Total',
    }))
    const open_amount_data = (data ?? []).map((d) => ({
      month: d.month,
      amount: d.open_amount,
      amountType: 'Open Amount',
    }))
    return [...invoice_total_data, ...open_amount_data]
  }, [data])

  const overdueAmount = useMemo((): number => {
    return (
      data?.reduce((acc, cur) => {
        if (dayjs(cur.month).isBefore(dayjs().startOf('month')) && cur.open_amount > 0)
          return acc + cur.open_amount
        else return acc
      }, 0) ?? 0
    )
  }, [data])

  const dueSoonAmount = useMemo((): number => {
    return (
      data?.reduce((acc, cur) => {
        if (
          dayjs(cur.month)
            .startOf('month')
            .isAfter(dayjs().startOf('month').subtract(1, 'month')) &&
          cur.open_amount > 0
        )
          return acc + cur.open_amount
        else return acc
      }, 0) ?? 0
    )
  }, [data])

  return (
    <motion.div className={'flex flex-1 flex-col pb-8 font-Spartan'}>
      <div className={'flex flex-1 flex-col gap-5'}>
        <div className="grid grid-cols-2 gap-5  w-full">
          <div className="flex flex-row justify-between border-2 rounded-md p-5">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-Spartan">Overdue</span>
              <span className="text-lg font-bold">{formatCurrency(overdueAmount)}</span>
            </div>
            <div></div>
          </div>
          <div className="flex flex-row justify-between border-2 rounded-md p-5">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-Spartan">Due Soon</span>
              <span className="text-lg font-bold">
                {formatCurrency(dueSoonAmount + overdueAmount)}
              </span>
            </div>
            <div></div>
          </div>
        </div>
        <div className="flex flex-col w-full border-2 rounded-md gap-5 p-5">
          <div className="flex flex-row justify-between">
            <span className="font-bold">Invoice History</span>
            <span className="font-bold text-lg">{formatCurrency(overdueAmount)}</span>
          </div>
          <div className="h-[270px]">
            <StackedBarChartV2
              data={remappedDataForBarChart}
              isLoading={isLoadingInvoicesHistory}
              groupKey="amountType"
              yKey={'amount'}
              xKey={'month'}
              customOptions={{
                plugins: {
                  formatter: (value: number): string => formatCurrency(value),
                  legend: {
                    display: true,
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-5 p-5 font-Spartan">
          <motion.div className={'flex flex-1 flex-col gap-3 pb-8'}>
            <div className={'flex flex-row justify-between gap-4'}>
              <span className="font-bold">Invoices List</span>
              <OutPortal node={filtersPortalNode} />
            </div>
            <FiltersContext
              portalNode={filtersPortalNode}
              setFilters={setFilters}
              defaultValues={filters}>
              <TextInput
                name={'month'}
                placeholder={'Month'}
                label={'Month'}
                type={'month'}
                defaultValue={filters.month}
              />
              <TextInput
                name={'name'}
                placeholder={'Name'}
                label={'Name'}
                type={'search'}
                defaultValue={''}
              />
            </FiltersContext>
            <Table
              defaultSorting={[{ id: 'month', desc: true }]}
              columns={columns}
              filters={{
                ...filters,
                agency_id,
              }}
              fetchFn={useGetAgencyInvoiceList}
            />
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

export default Invoices
